import { getConnectionInfo } from '@eluve/frontend-machine-utils';
import { CorrelatedLogger } from '@eluve/logger';

export const MINIMUM_DATA_RATE_BYTES_PER_SECOND = 1000;
export const SECOND_IN_MS = 1000;
const HEALTH_CHECK_INTERVAL_IN_MS = 15 * SECOND_IN_MS;
const DEFAULT_INTERVAL_DELAY_THRESHOLD_MULTIPLIER = 1.25;

export class AudioHealthCheck {
  private interval: NodeJS.Timeout | null = null;

  private healthCheckLastBytesReceived = 0;
  private healthCheckLastTime = Date.now();
  private readonly logger: CorrelatedLogger;
  private readonly healthCheckIntervalInMs;
  private readonly maxExpectedHealthCheckIntervalInMs;
  private readonly getTotalBytesReceived: () => number;
  private readonly getMediaStream: () => MediaStream | null;
  private readonly onHealthCheckFailed: (reason: string) => void;
  private readonly getAdditionalLogData?: () => Record<string, unknown>;
  private readonly isHealthy: () =>
    | { healthy: true }
    | { healthy: false; reason: string };

  constructor({
    getTotalBytesReceived,
    isHealthy,
    logger,
    healthCheckIntervalInMs = HEALTH_CHECK_INTERVAL_IN_MS,
    intervalDelayThresholdMultiplier = DEFAULT_INTERVAL_DELAY_THRESHOLD_MULTIPLIER,
    getAdditionalLogData,
    onHealthCheckFailed,
    getMediaStream,
  }: {
    getTotalBytesReceived: () => number;
    isHealthy: () => { healthy: true } | { healthy: false; reason: string };
    logger: CorrelatedLogger;
    healthCheckIntervalInMs?: number;
    intervalDelayThresholdMultiplier?: number;
    getAdditionalLogData?: () => Record<string, unknown>;
    onHealthCheckFailed: (reason: string) => void;
    getMediaStream: () => MediaStream | null;
  }) {
    this.getTotalBytesReceived = getTotalBytesReceived;
    this.isHealthy = isHealthy;
    this.logger = logger;
    this.healthCheckIntervalInMs = healthCheckIntervalInMs;
    this.getAdditionalLogData = getAdditionalLogData;
    this.onHealthCheckFailed = onHealthCheckFailed;
    this.getMediaStream = getMediaStream;
    this.maxExpectedHealthCheckIntervalInMs =
      healthCheckIntervalInMs * intervalDelayThresholdMultiplier;
  }

  start() {
    this.logger('info', 'Starting audio health check');
    this.interval = setInterval(() => {
      const connectionInfo = getConnectionInfo();
      const currentTime = Date.now();
      const elapsedMillisecondsSinceLastHealthCheck =
        currentTime - this.healthCheckLastTime;

      // Detect if browser suspended the tab/interval
      if (
        elapsedMillisecondsSinceLastHealthCheck >
        this.maxExpectedHealthCheckIntervalInMs
      ) {
        const percentageDelay =
          (elapsedMillisecondsSinceLastHealthCheck /
            this.healthCheckIntervalInMs -
            1) *
          100;

        this.logger(
          'warn',
          'Health check interval was delayed (browser suspension detected)',
          {
            elapsedMillisecondsSinceLastHealthCheck,
            expectedInterval: this.healthCheckIntervalInMs,
            maxExpectedInterval: this.maxExpectedHealthCheckIntervalInMs,
            percentageDelay,
          },
        );
      }

      const totalBytesReceived = this.getTotalBytesReceived();

      const bytesDiff = totalBytesReceived - this.healthCheckLastBytesReceived;
      const dataRate =
        bytesDiff / (elapsedMillisecondsSinceLastHealthCheck / 1000); // bytes per second

      // Expected data rate for audio should be much higher than this
      const minimumDataRate = MINIMUM_DATA_RATE_BYTES_PER_SECOND;
      const isDataRateTooLow = dataRate < minimumDataRate;

      const isReceivingData = totalBytesReceived > 0 && bytesDiff > 0;
      const isReceivingAudio = isReceivingData && !isDataRateTooLow;

      const additionalLogData = this.getAdditionalLogData?.() ?? {};

      const mediaStream = this.getMediaStream();
      const tracks = mediaStream
        ?.getAudioTracks()
        .map(({ readyState, enabled, kind, label }) => ({
          readyState,
          enabled,
          kind,
          label,
        }));

      const commonLogData = {
        ...additionalLogData,
        hasMediaStream: Boolean(mediaStream),
        tracks,
        totalBytesReceived,
        elapsedMillisecondsSinceLastHealthCheck,
        dataRate,
        bytesDiff,
        minimumDataRate,
        isDataRateTooLow,
        isReceivingData,
        isReceivingAudio,
        connectionDownlink: connectionInfo?.downlink,
        connectionEffectiveType: connectionInfo?.effectiveType,
      };

      const healthResult = this.isHealthy();
      if (healthResult.healthy === false) {
        this.logger('warn', 'Audio health check failed', {
          errorMessage: healthResult.reason,
          ...commonLogData,
        });

        this.onHealthCheckFailed(healthResult.reason);
      } else {
        this.logger('info', 'Audio health check passed', commonLogData);
      }

      this.healthCheckLastBytesReceived = totalBytesReceived;
      this.healthCheckLastTime = currentTime;
    }, this.healthCheckIntervalInMs);
  }

  stop() {
    this.logger('info', 'Stopping audio health check');
    if (this.interval) {
      clearInterval(this.interval);
      this.interval = null;
    }
  }
}
